import { createAsyncThunk } from "@reduxjs/toolkit";
import { requestsApi, type RequestListFilters } from "../api/requests.api";
import type { RequestsState } from "./requests.slice";
import type {
  AssignPayload,
  Attachment,
  CompletePayload,
  CreateRequestPayload,
  RejectPayload,
  RequestChangesPayload,
} from "../domain/request.types";
import {
  attachmentAdded,
  attachmentsLoaded,
  attachmentsRequestStarted,
  commentAdded,
  commentsLoaded,
  commentsRequestStarted,
  requestUpserted,
  requestsLoaded,
  requestsRequestFailed,
  requestsRequestStarted,
} from "./requests.slice";

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export const fetchRequests = createAsyncThunk<
  void,
  RequestListFilters | void,
  { state: { requests: RequestsState } }
>(
  "requests/fetchRequests",
  async (filters, { dispatch }) => {
    dispatch(requestsRequestStarted());
    try {
      const requests = await requestsApi.list(filters ?? {});
      dispatch(requestsLoaded(requests));
    } catch (err) {
      dispatch(requestsRequestFailed(errorMessage(err)));
      throw err;
    }
  },
  {
    // Every page needing requests (Dashboard, list, Kanban) calls this
    // unconditionally on mount, always unfiltered in practice - filtering on
    // the list page happens client-side against the already-fetched set.
    // With no cache, that's a full ~85KB refetch on every navigation. A
    // filtered call (not used today, but supported by the signature) always
    // goes to the network rather than risk serving a stale filtered subset.
    condition: (filters, { getState }) => {
      const hasFilters = filters && Object.keys(filters).length > 0;
      if (hasFilters) return true;
      const { status } = getState().requests;
      return status !== "succeeded" && status !== "loading";
    },
  },
);

export const fetchRequest = createAsyncThunk(
  "requests/fetchRequest",
  async (id: string, { dispatch }) => {
    const request = await requestsApi.get(id);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const createRequest = createAsyncThunk(
  "requests/create",
  async (payload: CreateRequestPayload, { dispatch }) => {
    const request = await requestsApi.create(payload);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const acceptRequest = createAsyncThunk(
  "requests/accept",
  async ({ id, payload }: { id: string; payload: AssignPayload }, { dispatch }) => {
    const request = await requestsApi.accept(id, payload);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const rejectRequest = createAsyncThunk(
  "requests/reject",
  async ({ id, payload }: { id: string; payload: RejectPayload }, { dispatch }) => {
    const request = await requestsApi.reject(id, payload);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const startRequest = createAsyncThunk("requests/start", async (id: string, { dispatch }) => {
  const request = await requestsApi.start(id);
  dispatch(requestUpserted(request));
  return request;
});

export const completeRequest = createAsyncThunk(
  "requests/complete",
  async ({ id, payload }: { id: string; payload: CompletePayload }, { dispatch }) => {
    const request = await requestsApi.complete(id, payload);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const requestReworkAction = createAsyncThunk(
  "requests/requestRework",
  async ({ id, payload }: { id: string; payload: RequestChangesPayload }, { dispatch }) => {
    const request = await requestsApi.requestRework(id, payload);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const approveReworkRequest = createAsyncThunk(
  "requests/approveRework",
  async ({ id, payload }: { id: string; payload: AssignPayload }, { dispatch }) => {
    const request = await requestsApi.approveRework(id, payload);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const rejectReworkRequest = createAsyncThunk(
  "requests/rejectRework",
  async (id: string, { dispatch }) => {
    const request = await requestsApi.rejectRework(id);
    dispatch(requestUpserted(request));
    return request;
  },
);

export const fetchComments = createAsyncThunk(
  "requests/fetchComments",
  async (requestId: string, { dispatch }) => {
    dispatch(commentsRequestStarted());
    const comments = await requestsApi.listComments(requestId);
    dispatch(commentsLoaded({ requestId, comments }));
  },
);

export const addComment = createAsyncThunk(
  "requests/addComment",
  async ({ requestId, body }: { requestId: string; body: string }, { dispatch }) => {
    const comment = await requestsApi.addComment(requestId, body);
    dispatch(commentAdded(comment));
  },
);

export const fetchAttachments = createAsyncThunk(
  "requests/fetchAttachments",
  async (requestId: string, { dispatch }) => {
    dispatch(attachmentsRequestStarted());
    const attachments = await requestsApi.listAttachments(requestId);
    dispatch(attachmentsLoaded({ requestId, attachments }));
  },
);

export const uploadAttachment = createAsyncThunk(
  "requests/uploadAttachment",
  async (
    {
      requestId,
      file,
      category,
    }: { requestId: string; file: File; category: Attachment["category"] },
    { dispatch },
  ) => {
    const attachment = await requestsApi.uploadAttachment(requestId, file, category);
    dispatch(attachmentAdded(attachment));
    return attachment;
  },
);
